/**
 * CREDIT OPTIMIZATION ADVISOR
 * 
 * Intelligent recommendations for maximizing credit score improvement:
 * - Balance transfer suggestions (redistribute debt optimally)
 * - Pay-down priorities (which cards to pay first)
 * - Credit limit increase opportunities
 * - Utilization threshold strategies
 * - General score improvement tips
 * 
 * Used in: PreScreening.jsx, CreditReportAnalyzer.jsx
 */

import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import {
  ArrowRight,
  ArrowLeftRight,
  TrendingUp,
  TrendingDown,
  CreditCard,
  DollarSign,
  Lightbulb,
  Target,
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Zap,
  Calculator,
  Percent,
  Info,
  Star
} from "lucide-react";

// Utilization thresholds and their impact
const UTILIZATION_THRESHOLDS = {
  EXCELLENT: 9,    // <10% - Best for score
  GOOD: 29,        // <30% - Recommended
  FAIR: 49,        // <50% - Acceptable
  POOR: 74,        // <75% - Hurting score
  VERY_POOR: 89,   // <90% - Significantly hurting
  MAXED: 100       // 90%+ - Severely hurting
};

const getUtilizationStatus = (util) => {
  if (util <= UTILIZATION_THRESHOLDS.EXCELLENT) return { label: "Excellent", color: "text-green-400", bg: "bg-green-500/20" };
  if (util <= UTILIZATION_THRESHOLDS.GOOD) return { label: "Good", color: "text-emerald-400", bg: "bg-emerald-500/20" };
  if (util <= UTILIZATION_THRESHOLDS.FAIR) return { label: "Fair", color: "text-blue-400", bg: "bg-blue-500/20" };
  if (util <= UTILIZATION_THRESHOLDS.POOR) return { label: "Poor", color: "text-amber-400", bg: "bg-amber-500/20" };
  if (util <= UTILIZATION_THRESHOLDS.VERY_POOR) return { label: "Very Poor", color: "text-orange-400", bg: "bg-orange-500/20" };
  return { label: "Critical", color: "text-red-400", bg: "bg-red-500/20" };
};

/**
 * Calculate optimal balance distribution across cards
 * Goal: Get all cards to same utilization % (ideally <10%)
 */
const calculateOptimalDistribution = (cards) => {
  if (!cards || cards.length === 0) return null;
  
  const totalBalance = cards.reduce((sum, c) => sum + (c.balance || c.current_balance || 0), 0);
  const totalLimit = cards.reduce((sum, c) => sum + (c.limit || c.credit_limit || 0), 0);
  
  if (totalLimit === 0) return null;
  
  const targetUtilization = (totalBalance / totalLimit) * 100;
  
  // Calculate what each card's balance should be for equal utilization
  const optimalDistribution = cards.map(card => {
    const limit = card.limit || card.credit_limit || 0;
    const currentBalance = card.balance || card.current_balance || 0;
    const optimalBalance = Math.round((targetUtilization / 100) * limit);
    const currentUtil = limit > 0 ? (currentBalance / limit) * 100 : 0;
    
    return {
      ...card,
      name: card.name || card.card_name || "Credit Card",
      limit,
      currentBalance,
      currentUtil: Math.round(currentUtil),
      optimalBalance,
      difference: currentBalance - optimalBalance,
      needsTransferOut: currentBalance > optimalBalance,
      needsTransferIn: currentBalance < optimalBalance
    };
  });
  
  return {
    totalBalance,
    totalLimit,
    currentOverallUtil: Math.round((totalBalance / totalLimit) * 100),
    targetUtilization: Math.round(targetUtilization),
    distribution: optimalDistribution
  };
};

/**
 * Generate balance transfer recommendations
 */
const generateBalanceTransfers = (cards) => {
  if (!cards || cards.length < 2) return [];
  
  const transfers = [];
  const distribution = calculateOptimalDistribution(cards);
  
  if (!distribution) return [];
  
  // Find cards that need to transfer OUT (high util) and IN (low util)
  const highUtilCards = distribution.distribution
    .filter(c => c.needsTransferOut && c.difference > 50) // At least $50 to transfer
    .sort((a, b) => b.currentUtil - a.currentUtil); // Highest util first
    
  const lowUtilCards = distribution.distribution
    .filter(c => c.needsTransferIn && Math.abs(c.difference) > 50)
    .sort((a, b) => a.currentUtil - b.currentUtil); // Lowest util first
  
  // Match high util cards with low util cards
  for (const highCard of highUtilCards) {
    for (const lowCard of lowUtilCards) {
      if (highCard.name === lowCard.name) continue;
      
      const availableToTransfer = highCard.difference;
      const canAccept = Math.abs(lowCard.difference);
      const transferAmount = Math.min(availableToTransfer, canAccept);
      
      if (transferAmount >= 100) { // Minimum $100 transfer to be worth it
        const fromUtilAfter = highCard.limit > 0 
          ? Math.round(((highCard.currentBalance - transferAmount) / highCard.limit) * 100)
          : 0;
        const toUtilAfter = lowCard.limit > 0
          ? Math.round(((lowCard.currentBalance + transferAmount) / lowCard.limit) * 100)
          : 0;
        
        transfers.push({
          from: highCard.name,
          to: lowCard.name,
          amount: transferAmount,
          fromUtilBefore: highCard.currentUtil,
          fromUtilAfter,
          toUtilBefore: lowCard.currentUtil,
          toUtilAfter,
          impact: `${highCard.name}: ${highCard.currentUtil}% → ${fromUtilAfter}%`,
          estimatedScoreGain: calculateUtilizationScoreImpact(highCard.currentUtil, fromUtilAfter)
        });
        
        // Update the balances for next iteration
        highCard.currentBalance -= transferAmount;
        highCard.difference -= transferAmount;
        lowCard.currentBalance += transferAmount;
        lowCard.difference += transferAmount;
      }
    }
  }
  
  return transfers.slice(0, 5); // Limit to top 5 recommendations
};

/**
 * Calculate estimated score impact from utilization change
 */
const calculateUtilizationScoreImpact = (fromUtil, toUtil) => {
  // Rough estimates based on FICO scoring
  const getUtilPenalty = (util) => {
    if (util <= 9) return 0;
    if (util <= 29) return -15;
    if (util <= 49) return -35;
    if (util <= 74) return -55;
    if (util <= 89) return -75;
    return -100;
  };
  
  const beforePenalty = getUtilPenalty(fromUtil);
  const afterPenalty = getUtilPenalty(toUtil);
  
  return Math.abs(beforePenalty - afterPenalty);
};

/**
 * Generate pay-down priority recommendations
 */
const generatePayDownPriorities = (cards) => {
  if (!cards || cards.length === 0) return [];
  
  const priorities = [];
  
  // Sort cards by utilization (highest first)
  const sortedCards = [...cards]
    .map(card => ({
      name: card.name || card.card_name || "Credit Card",
      balance: card.balance || card.current_balance || 0,
      limit: card.limit || card.credit_limit || 0,
      util: (card.limit || card.credit_limit) > 0 
        ? Math.round(((card.balance || card.current_balance || 0) / (card.limit || card.credit_limit)) * 100)
        : 0
    }))
    .filter(c => c.limit > 0 && c.balance > 0)
    .sort((a, b) => b.util - a.util);
  
  for (const card of sortedCards) {
    // Find next threshold to cross
    let targetUtil = 9; // Default to excellent
    let targetLabel = "Excellent (<10%)";
    
    if (card.util > 89) {
      targetUtil = 89;
      targetLabel = "Below 90%";
    } else if (card.util > 74) {
      targetUtil = 74;
      targetLabel = "Below 75%";
    } else if (card.util > 49) {
      targetUtil = 49;
      targetLabel = "Below 50%";
    } else if (card.util > 29) {
      targetUtil = 29;
      targetLabel = "Below 30%";
    } else if (card.util > 9) {
      targetUtil = 9;
      targetLabel = "Below 10%";
    } else {
      continue; // Already excellent
    }
    
    const targetBalance = Math.round((targetUtil / 100) * card.limit);
    const payDownAmount = card.balance - targetBalance;
    const scoreGain = calculateUtilizationScoreImpact(card.util, targetUtil);
    
    if (payDownAmount > 0) {
      priorities.push({
        card: card.name,
        currentUtil: card.util,
        targetUtil,
        targetLabel,
        currentBalance: card.balance,
        targetBalance,
        payDownAmount,
        estimatedScoreGain: scoreGain,
        priority: card.util > 74 ? "HIGH" : card.util > 49 ? "MEDIUM" : "LOW"
      });
    }
  }
  
  return priorities.slice(0, 5);
};

/**
 * Generate general credit improvement recommendations
 */
const generateGeneralRecommendations = (cards, currentScore, negativeAccounts = []) => {
  const recommendations = [];
  
  // Calculate overall utilization
  const totalBalance = cards?.reduce((sum, c) => sum + (c.balance || c.current_balance || 0), 0) || 0;
  const totalLimit = cards?.reduce((sum, c) => sum + (c.limit || c.credit_limit || 0), 0) || 0;
  const overallUtil = totalLimit > 0 ? (totalBalance / totalLimit) * 100 : 0;
  
  // High utilization recommendations
  if (overallUtil > 30) {
    recommendations.push({
      title: "Request Credit Limit Increases",
      description: "Call your card issuers and request limit increases. This instantly lowers your utilization without paying down balances. Best success: accounts in good standing for 6+ months.",
      impact: "Could add +20-40 points",
      priority: "HIGH",
      icon: "TrendingUp"
    });
  }
  
  // Payment timing recommendation
  if (overallUtil > 10) {
    recommendations.push({
      title: "Pay Before Statement Closes",
      description: "Pay down your balance BEFORE your statement closing date (not just the due date). This is what gets reported to credit bureaus. Check your statement date and pay a few days before.",
      impact: "Immediate utilization improvement",
      priority: "MEDIUM",
      icon: "Calendar"
    });
  }
  
  // Multiple high-balance cards
  const highUtilCards = cards?.filter(c => {
    const util = (c.limit || c.credit_limit) > 0 
      ? ((c.balance || c.current_balance || 0) / (c.limit || c.credit_limit)) * 100 
      : 0;
    return util > 50;
  }) || [];
  
  if (highUtilCards.length >= 2) {
    recommendations.push({
      title: "Consider a Balance Transfer Card",
      description: "With multiple high-utilization cards, a 0% APR balance transfer card could consolidate debt AND add a new card with $0 balance, lowering overall utilization.",
      impact: "+10-30 points from new card + lower util",
      priority: "MEDIUM",
      icon: "CreditCard"
    });
  }
  
  // Negative accounts recommendations
  if (negativeAccounts && negativeAccounts.length > 0) {
    const collections = negativeAccounts.filter(a => 
      (a.status || '').toLowerCase().includes('collection') ||
      (a.account_type || '').toLowerCase().includes('collection')
    );
    
    if (collections.length > 0) {
      recommendations.push({
        title: "Negotiate Pay-for-Delete",
        description: `You have ${collections.length} collection(s). Contact each collector and offer to pay in exchange for complete removal from your credit report. Get the agreement in writing BEFORE paying.`,
        impact: "+20-50 points per removal",
        priority: "HIGH",
        icon: "Handshake"
      });
    }
    
    const chargeoffs = negativeAccounts.filter(a => 
      (a.status || '').toLowerCase().includes('charge') ||
      (a.account_type || '').toLowerCase().includes('charge-off')
    );
    
    if (chargeoffs.length > 0) {
      recommendations.push({
        title: "Address Charge-Offs Strategically",
        description: `You have ${chargeoffs.length} charge-off(s). Options: (1) Dispute any inaccuracies, (2) Negotiate pay-for-delete, or (3) Let them age off after 7 years. Paying without deletion does NOT remove them.`,
        impact: "+25-60 points if removed",
        priority: "HIGH",
        icon: "AlertTriangle"
      });
    }
  }
  
  // Low score specific recommendations
  if (currentScore && currentScore < 650) {
    recommendations.push({
      title: "Consider a Secured Credit Card",
      description: "A secured card with a small deposit ($200-500) can help rebuild credit. Use it for small purchases and pay in full each month. After 6-12 months, you may graduate to an unsecured card.",
      impact: "Builds positive history",
      priority: "MEDIUM",
      icon: "Shield"
    });
  }
  
  // Don't close old accounts
  if (cards && cards.length > 1) {
    recommendations.push({
      title: "Keep Old Cards Open",
      description: "Don't close old credit cards, even if unused. They help your credit age and available credit. Use them for a small purchase every 6 months to keep them active.",
      impact: "Protects credit age & utilization",
      priority: "LOW",
      icon: "Clock"
    });
  }
  
  // Authorized user strategy
  if (currentScore && currentScore < 700) {
    recommendations.push({
      title: "Become an Authorized User",
      description: "Ask a family member with excellent credit to add you as an authorized user on an old, low-utilization card. Their payment history can boost your score quickly.",
      impact: "+20-50 points possible",
      priority: "MEDIUM",
      icon: "Users"
    });
  }
  
  return recommendations;
};

/**
 * Main Credit Optimization Advisor Component
 */
export default function CreditOptimizationAdvisor({ 
  creditCards = [], 
  currentScore = null,
  negativeAccounts = [],
  showTitle = true,
  compact = false 
}) {
  const [expandedSection, setExpandedSection] = useState("transfers");
  
  // Generate all recommendations
  const balanceTransfers = useMemo(() => generateBalanceTransfers(creditCards), [creditCards]);
  const payDownPriorities = useMemo(() => generatePayDownPriorities(creditCards), [creditCards]);
  const generalRecommendations = useMemo(
    () => generateGeneralRecommendations(creditCards, currentScore, negativeAccounts), 
    [creditCards, currentScore, negativeAccounts]
  );
  const distribution = useMemo(() => calculateOptimalDistribution(creditCards), [creditCards]);
  
  // Calculate totals
  const totalBalance = creditCards.reduce((sum, c) => sum + (c.balance || c.current_balance || 0), 0);
  const totalLimit = creditCards.reduce((sum, c) => sum + (c.limit || c.credit_limit || 0), 0);
  const overallUtil = totalLimit > 0 ? Math.round((totalBalance / totalLimit) * 100) : 0;
  const utilStatus = getUtilizationStatus(overallUtil);
  
  // No cards to analyze
  if (!creditCards || creditCards.length === 0) {
    return (
      <Card className="bg-zinc-800/50 border-zinc-700">
        <CardContent className="p-4 text-center">
          <Lightbulb className="w-8 h-8 text-amber-400 mx-auto mb-2" />
          <p className="text-zinc-400 text-sm">Add credit card information to get personalized optimization recommendations.</p>
        </CardContent>
      </Card>
    );
  }
  
  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };
  
  const getPriorityBadge = (priority) => {
    switch (priority) {
      case "HIGH":
        return <Badge className="bg-red-500/20 text-red-400 border-red-500/30">High Priority</Badge>;
      case "MEDIUM":
        return <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">Medium</Badge>;
      case "LOW":
        return <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">Low</Badge>;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-4">
      {showTitle && (
        <div className="flex items-center gap-2 mb-4">
          <Lightbulb className="w-5 h-5 text-amber-400" />
          <h3 className="text-lg font-semibold text-white">Credit Optimization Advisor</h3>
        </div>
      )}
      
      {/* Current Utilization Overview */}
      <Card className={`${utilStatus.bg} border-zinc-700`}>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-zinc-400 text-sm">Overall Credit Utilization</p>
              <div className="flex items-center gap-2 mt-1">
                <span className={`text-2xl font-bold ${utilStatus.color}`}>{overallUtil}%</span>
                <Badge className={`${utilStatus.bg} ${utilStatus.color}`}>{utilStatus.label}</Badge>
              </div>
              <p className="text-xs text-zinc-500 mt-1">
                ${totalBalance.toLocaleString()} used of ${totalLimit.toLocaleString()} available
              </p>
            </div>
            <div className="text-right">
              <p className="text-zinc-400 text-sm">Target</p>
              <p className="text-green-400 text-xl font-bold">&lt;10%</p>
              <p className="text-xs text-zinc-500">for best score</p>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Balance Transfer Recommendations */}
      {balanceTransfers.length > 0 && (
        <Card className="bg-zinc-800/50 border-zinc-700">
          <CardHeader 
            className="pb-2 cursor-pointer" 
            onClick={() => toggleSection("transfers")}
          >
            <div className="flex items-center justify-between">
              <CardTitle className="text-white flex items-center gap-2 text-base">
                <ArrowLeftRight className="w-5 h-5 text-purple-400" />
                Balance Transfer Strategy
                <Badge className="bg-purple-500/20 text-purple-400">{balanceTransfers.length}</Badge>
              </CardTitle>
              {expandedSection === "transfers" ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </div>
          </CardHeader>
          {expandedSection === "transfers" && (
            <CardContent className="space-y-3">
              <p className="text-xs text-zinc-400 mb-3">
                Move balances from high-utilization cards to low-utilization cards to optimize your score:
              </p>
              {balanceTransfers.map((transfer, idx) => (
                <div key={idx} className="bg-zinc-900/50 rounded-lg p-3 border border-purple-500/20">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-white font-medium text-sm">{transfer.from}</span>
                    <ArrowRight className="w-4 h-4 text-purple-400" />
                    <span className="text-white font-medium text-sm">{transfer.to}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-purple-400 font-bold">${transfer.amount.toLocaleString()}</p>
                      <p className="text-xs text-zinc-500">
                        {transfer.fromUtilBefore}% → {transfer.fromUtilAfter}%
                      </p>
                    </div>
                    <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                      +{transfer.estimatedScoreGain} pts est.
                    </Badge>
                  </div>
                </div>
              ))}
              <div className="bg-amber-500/10 border border-amber-500/30 rounded p-2 mt-2">
                <p className="text-amber-400 text-xs flex items-start gap-1">
                  <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                  Check for balance transfer fees (usually 3-5%) and promotional APR offers before transferring.
                </p>
              </div>
            </CardContent>
          )}
        </Card>
      )}
      
      {/* Pay-Down Priorities */}
      {payDownPriorities.length > 0 && (
        <Card className="bg-zinc-800/50 border-zinc-700">
          <CardHeader 
            className="pb-2 cursor-pointer" 
            onClick={() => toggleSection("paydown")}
          >
            <div className="flex items-center justify-between">
              <CardTitle className="text-white flex items-center gap-2 text-base">
                <Target className="w-5 h-5 text-blue-400" />
                Pay-Down Priorities
                <Badge className="bg-blue-500/20 text-blue-400">{payDownPriorities.length}</Badge>
              </CardTitle>
              {expandedSection === "paydown" ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </div>
          </CardHeader>
          {expandedSection === "paydown" && (
            <CardContent className="space-y-3">
              <p className="text-xs text-zinc-400 mb-3">
                Focus payments on these cards to cross key utilization thresholds:
              </p>
              {payDownPriorities.map((item, idx) => (
                <div key={idx} className="bg-zinc-900/50 rounded-lg p-3 border border-blue-500/20">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-white font-medium text-sm">{item.card}</span>
                    {getPriorityBadge(item.priority)}
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div>
                      <p className="text-xs text-zinc-500">Current</p>
                      <p className={`font-bold ${getUtilizationStatus(item.currentUtil).color}`}>
                        {item.currentUtil}%
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-zinc-500">Pay Down</p>
                      <p className="font-bold text-amber-400">${item.payDownAmount.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-xs text-zinc-500">Target</p>
                      <p className="font-bold text-green-400">{item.targetLabel}</p>
                    </div>
                  </div>
                  <div className="mt-2 flex justify-end">
                    <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                      +{item.estimatedScoreGain} pts est.
                    </Badge>
                  </div>
                </div>
              ))}
            </CardContent>
          )}
        </Card>
      )}
      
      {/* General Recommendations */}
      {generalRecommendations.length > 0 && (
        <Card className="bg-zinc-800/50 border-zinc-700">
          <CardHeader 
            className="pb-2 cursor-pointer" 
            onClick={() => toggleSection("general")}
          >
            <div className="flex items-center justify-between">
              <CardTitle className="text-white flex items-center gap-2 text-base">
                <Star className="w-5 h-5 text-amber-400" />
                Score Improvement Tips
                <Badge className="bg-amber-500/20 text-amber-400">{generalRecommendations.length}</Badge>
              </CardTitle>
              {expandedSection === "general" ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </div>
          </CardHeader>
          {expandedSection === "general" && (
            <CardContent className="space-y-3">
              {generalRecommendations.map((rec, idx) => (
                <div key={idx} className="bg-zinc-900/50 rounded-lg p-3 border border-amber-500/20">
                  <div className="flex items-start gap-3">
                    <div className="bg-amber-500/20 rounded-full p-2 flex-shrink-0">
                      <Lightbulb className="w-4 h-4 text-amber-400" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-white font-medium text-sm">{rec.title}</p>
                        {getPriorityBadge(rec.priority)}
                      </div>
                      <p className="text-xs text-zinc-400 mb-2">{rec.description}</p>
                      <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-xs">
                        {rec.impact}
                      </Badge>
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          )}
        </Card>
      )}
      
      {/* Quick Stats */}
      {!compact && distribution && (
        <Card className="bg-zinc-800/50 border-zinc-700">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Calculator className="w-4 h-4 text-zinc-400" />
              <span className="text-sm text-zinc-400">If all cards were at equal utilization:</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {distribution.distribution.slice(0, 4).map((card, idx) => (
                <div key={idx} className="bg-zinc-900/50 rounded p-2 text-center">
                  <p className="text-xs text-zinc-500 truncate">{card.name}</p>
                  <p className={`text-sm font-bold ${getUtilizationStatus(card.currentUtil).color}`}>
                    {card.currentUtil}%
                  </p>
                  <p className="text-xs text-zinc-600">→ {distribution.targetUtilization}%</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// Export utility functions for use in other components
export { 
  calculateOptimalDistribution, 
  generateBalanceTransfers, 
  generatePayDownPriorities,
  generateGeneralRecommendations,
  getUtilizationStatus,
  calculateUtilizationScoreImpact
};
